import storage from '../../../../../hooks/useStorage/useStorage';
import {
  CustomSnapshot,
  CustomSnapshotByOrder,
  RemoveOrderSnapshotParams,
  SnapshotStorageTarget,
  WriteSnapshotsParams,
} from './useSnapshotController.interface';

export const NO_ACTIVE_ORDER_KEY = '__noActiveOrder__';

const isCustomSnapshot = (value: unknown): value is CustomSnapshot<unknown> =>
  typeof value === 'object' && value !== null && 'id' in value;

const parseSnapshots = <T>(value: unknown): CustomSnapshotByOrder<T> => {
  if (typeof value !== 'object' || value === null) {
    return {};
  }

  if (isCustomSnapshot(value) || 'snapshot' in value) {
    return {};
  }

  const entries = Object.entries(value).filter(([, entry]) => isCustomSnapshot(entry));

  return Object.fromEntries(entries) as CustomSnapshotByOrder<T>;
};

const readSharedEntitySnapshots = <T>(sharedEntityTeamId: string): CustomSnapshotByOrder<T> => {
  const stored = localStorage.getItem(sharedEntityTeamId);

  if (!stored) {
    return {};
  }

  try {
    return parseSnapshots<T>(JSON.parse(stored));
  } catch {
    return {};
  }
};

export const readSnapshots = <T>({
  teamId,
  sharedEntityTeamId,
  isOfflineMode,
}: SnapshotStorageTarget): CustomSnapshotByOrder<T> => {
  if (sharedEntityTeamId) {
    return readSharedEntitySnapshots<T>(sharedEntityTeamId);
  }

  return parseSnapshots<T>(storage.getCustomCatalogSnapshot(teamId, isOfflineMode));
};

export const writeSnapshots = <T>({
  snapshots,
  teamId,
  sharedEntityTeamId,
  isOfflineMode,
}: WriteSnapshotsParams<T>) => {
  if (sharedEntityTeamId) {
    localStorage.setItem(sharedEntityTeamId, JSON.stringify(snapshots));
    return;
  }

  storage.setCustomCatalogSnapshot<T>({ snapshots, teamId, isOfflineMode });
};

export const removeOrderSnapshot = ({ orderId, ...target }: RemoveOrderSnapshotParams) => {
  const snapshots = readSnapshots<unknown>(target);

  if (!(orderId in snapshots)) {
    return;
  }

  const { [orderId]: removedSnapshot, ...remainingSnapshots } = snapshots;

  writeSnapshots({ snapshots: remainingSnapshots, ...target });
};
